import { satisfies } from 'semver';

import { isJasperFirmware } from './firmware';
import { HardwareDescription } from './hardware-description';
import {
  ProductGroup,
  findSomanetProductById,
  isEncoderPortInternal,
  isEncoderPortUsable,
  isSomanetProductIdInRange,
  resolveEncoderProductId,
} from './product';

/**
 * Represents a detailed error related to an encoder register.
 */
export interface EncoderRegisterError {
  /**
   * A short, human-readable name for the error.
   */
  name: string;

  /**
   * A brief error code or message identifying the error type.
   */
  error: string;

  /**
   * A concise description of the error condition.
   */
  description: string;

  /**
   * Suggested actions or steps to resolve or remediate the error.
   */
  remedy: string;

  /**
   * A comprehensive explanation of the error, including context and details.
   */
  fullDescription: string;
}

/**
 * Enum of Circulo encoder register addresses.
 *
 * These constants represent the addresses of various encoder registers
 * used for status monitoring and data reading.
 */
export enum CirculoEncoderRegisterAddress {
  STATUS0 = 0x76,
  STATUS1 = 0x77,
  OUT_ZERO_OUT_MSB = 0x11,
}

/**
 * Lookup table mapping encoder register error bits to detailed error information.
 *
 * Each element in the array corresponds to a specific encoder status register (e.g., STATUS0, STATUS1),
 * identified by its index. Within each object, the keys represent bit flags (powers of two) indicating
 * specific error conditions. The associated value is an `EncoderRegisterError` object describing
 * the error, its cause, and suggested remedy.
 *
 * Example usage:
 * ```ts
 * const errorsForStatus0 = circuloEncoderRegisterErrors[0][1]; // Gets the error for bit 1 of STATUS0
 * ```
 */
export const circuloEncoderRegisterErrors: { [key: number]: EncoderRegisterError }[] = [
  {
    1: {
      name: 'AM_MIN',
      error: 'Signal error: poor level (master track)',
      description: 'Magnetic field strength is too low for encoder operation.',
      remedy: 'Check positioning of the encoder ring.',
      fullDescription:
        "An encoder magnetic ring is too far from the encoder chip or isn't detected. Check the mechanical assembly. In some rare cases can be provoked by demagnetized ring or field strength drop due to high temperatures. A magnetic ring is too far from the encoder chip. Check the mechanical assembly.",
    },
    2: {
      name: 'AM_MAX',
      error: 'Signal error: clipping (master track)',
      description: 'Magnetic field strength is too high.',
      remedy: 'Check mechanical assembly and external fields.',
      fullDescription:
        "The outer track of the encoder chip detects too strong a field. Typically, it can't be generated by the magnetic ring itself at normal conditions. Please check the influence of external fields, especially brakes. A magnetic ring is too close to the encoder chip. Check the mechanical assembly.",
    },
    4: {
      name: 'AN_MIN',
      error: 'Signal error: poor level (nonius track)',
      description: 'Magnetic field strength is too low for encoder operation.',
      remedy: 'Check positioning of the encoder ring.',
      fullDescription:
        "An encoder magnetic ring is too far from the encoder chip or isn't detected. Check the mechanical assembly. In some rare cases can be provoked by demagnetized ring or field strength drop due to high temperatures.",
    },
    8: {
      name: 'AN_MAX',
      error: 'Signal error: clipping (nonius track)',
      description: 'Magnetic field strength is too high.',
      remedy: 'Check mechanical assembly and external fields.',
      fullDescription:
        "The inner track of the encoder chip detects too strong a field. Typically, it can't be generated by the magnetic ring itself at normal conditions. Please check the influence of external fields, especially brakes.",
    },
    16: {
      name: 'STUP',
      error: 'Startup iC-MU',
      description: 'Encoder startup error. Can appear during Multiturn configuration.',
      remedy: 'Power cycle the drive.',
      fullDescription:
        'Encoder initialization error. The typical reason is: not saved Multiturn Counter configuration. Connect the multiturn battery, check the battery voltage, configure the multiturn bits in OBLAC Drives, configure integrated encoder and save changes. Power cycle the drive. If the error is always present, contact support.',
    },
  },
  {
    2: {
      name: 'FRQ_CNV',
      error: 'Excessive signal frequency for internal 12 Bit converter',
      description: 'Encoder maximum speed was exceeded.',
      remedy: '',
      fullDescription: 'Encoder maximum speed was exceeded. Circulo 7 - 12000 RPM or Circulo 9 - 6000 RPM',
    },
    4: {
      name: 'FRQ_ABZ',
      error: 'Excessive signal frequency for ABZ-converter',
      description: 'Encoder maximum speed was exceeded.',
      remedy: '',
      fullDescription: 'Encoder maximum speed was exceeded. Circulo 7 - 12000 RPM or Circulo 9 - 6000 RPM',
    },
    8: {
      name: 'NON_CTR',
      error: 'Period counter consistency error: counted period <-> calculated Nonius position',
      description: 'Integrated Encoder calibration parameters are outdated or non-existing.',
      remedy: 'Please calibrate the integrated encoder.',
      fullDescription:
        "The absolute position value can't be computed reliably at the current position. This can be caused by: encoder wasn't calibrated, change of encoder mechanics, severe thermal expansion or magnetic target damage. Run diagnostics and repeat the calibration procedure. If this error prevents you from performing calibration - disable it or contact support.",
    },
    16: {
      name: 'MT_CTR',
      error: 'Multiturn data consistency error: counted multiturn <-> external MT data',
      description: 'Multiturn Counter synchronization error.',
      remedy: 'Check MT configuration, connect a battery. Power cycle the drive.',
      fullDescription:
        'Can happen because of not saved Multiturn Counter configuration. Connect the multiturn battery, check the battery voltage, configure the multiturn bits in OBLAC Drives, configure integrated encoder and save changes. If the error is still present, please contact support.',
    },
    32: {
      name: 'MT_ERR',
      error:
        'Multiturn communication error: MTD line not 1 when trying to read MT data, MTD line is not 0 right after the last clock pulse, or SSI error bit active on MT interface',
      description: 'Multiturn Counter communication error.',
      remedy: 'Check MT configuration, connect a battery. Power cycle the drive.',
      fullDescription:
        'The Multiturn counter is in error state. Connect the multiturn battery, check the battery voltage, configure the multiturn bits in OBLAC Drives, configure integrated encoder and save changes. Power cycle the drive. If the error is still present, please contact support.',
    },
    64: {
      name: 'EPR_ERR',
      error: 'I2C communication error: No EEPROM or I2C communication error',
      description: 'Encoder EEPROM memory communication error.',
      remedy: 'Power Cycle the drive.',
      fullDescription:
        "Communication with the EEPROM memory of the chip is distorted. Power Cycle the drive. If this can't be reset, there is a hardware problem. Please contact support.",
    },
    128: {
      name: '',
      error: 'Invalid check sum internal RAM',
      description: 'Invalid check sum internal RAM',
      remedy: '',
      fullDescription: '',
    },
  },
  {
    1: {
      name: 'STUP_ERR',
      error: 'Startup error',
      description:
        'Erroneous startup procedure, e.g., I2C stuck-at, EEPROM read error or invalid CRC checksum stored in the EEPROM.',
      remedy: 'Reboot iC-PVL.',
      fullDescription:
        'Erroneous startup procedure, e.g., I2C stuck-at, EEPROM read error or invalid CRC checksum stored in the EEPROM. No position acquisition is performed. Interfaces are blocked. Please revise configuration and checksums or replace EEPROM. Reboot iC-PVL.',
    },
    2: {
      name: 'CFG_ERR',
      error: 'Internal configuration error',
      description:
        'This error is raised when the configuration stored in the internal RAM doesn’t match the CRC checksum.',
      remedy: 'Reboot iC-PVL.',
      fullDescription:
        'This error is raised when the configuration stored in the internal RAM doesn’t match the CRC checksum. This can be caused for example by changing the configuration data without updating the checksum accordingly, or an unexpected level flip of one or more bits in the RAM configuration area. Position is invalid. Reboot iC-PVL. It can also be caused by writing to RAM configuration via I2C. CFG_ERR can be cleared by a SCLR command.',
    },
    4: {
      name: 'CTR_ERR',
      error: 'Internal counter error',
      description: 'The value stored in the internal multiturn counter doesn’t match the CRC checksum.',
      remedy: 'Reboot iC-PVL.',
      fullDescription:
        'The value stored in the internal multiturn counter doesn’t match the CRC checksum. This can be caused for example by changing the multiturn counter value without updating the checksum accordingly, or and unexpected level flip of one or more bits in the multiturn counter RAM area. Position is invalid. Reboot iC-PVL. It can also be caused by writing to MT_COUNT via I2C. CTR_ERR can be cleared by a SCLR command.',
    },
    8: {
      name: 'POS_ERR',
      error: 'Position error',
      description:
        'The position encoding observed an unexpected position jump, caused e.g. by excessive speed or excessive acceleration of the magnetic disc or tape.',
      remedy: 'Reboot iC-PVL.',
      fullDescription:
        'The position encoding observed an unexpected position jump, caused e.g. by excessive speed or excessive acceleration of the magnetic disc or tape. Alternatively, this error bit is set on weak, disturbed magnetic signals or complete loss of magnet. Position is invalid. Optimize magnet position and cross-check angular velocity/acceleration. Reboot iC-PVL.',
    },
    16: {
      name: 'BAT_ERR',
      error: 'Battery error',
      description: 'Battery undervoltage according to Elec. Char. No. 404.',
      remedy: 'Reboot iC-PVL.',
      fullDescription:
        'Battery undervoltage according to Elec. Char. No. 404. Position is invalid. Change battery. Reboot iC-PVL. Battery monitoring is active during VDD supply.',
    },
    32: {
      name: 'BAT_WRN',
      error: 'Battery early warning',
      description: 'Battery voltage early warning according to Elec. Char. No. 405.',
      remedy: 'Switch off system, change battery and restart.',
      fullDescription:
        'Battery voltage early warning according to Elec. Char. No. 405. Battery may be changed during main supply (VDD) as soon as possible. Alternatively, halt system, read current position and restore it. Switch off system, change battery and restart. Restored position may be set as counter preload. Battery monitoring is active during VDD supply.',
    },
    64: {
      name: 'PDR',
      error: 'Power down reset detected',
      description:
        'A power down reset was performed, caused by undervoltage considerably lower than the battery error threshold.',
      remedy: '',
      fullDescription:
        'A power down reset was performed, caused by undervoltage considerably lower than the battery error threshold. Another reason may be due to insufficient stabilizing capacitors at the supply lines VDD and VBAT. If a power down reset is performed, all internal circuitry is initialized to its default state. Counter position is set to zero and the working state is reset to the POWON State. For power down reset, the voltage at VDDS is relevant. Therefore, it is only performed if both, VDD and VBAT are low. The voltage threshold is typically between 1.5 V and 2.5 V.',
    },
    128: {
      name: 'PRESET',
      error: 'Pin preset detected',
      description:
        'This bit is set whenever the chip is coming from a power-down condition or a pin preset has been detected.',
      remedy: '',
      fullDescription:
        'This bit is set whenever the chip is coming from a power-down condition or a pin preset has been detected. It may be cleared by issuing a SCLR or RESET command. This can be interpreted as an acknowledgment that the power-down condition or pin preset was intentional. If the bit is set later during field operation, it is most likely unintentional. The cause may have been an external disruption, a short circuit error on the PRE line or a complete loss of power supply (VDD and VBAT). The PRESET bit is also set if the sleep mode has been enabled or an i2C REBOOT has been performed.',
    },
  },
];

/**
 * Maps a status register value to a list of corresponding encoder register errors.
 *
 * This function interprets each set bit in the given 8-bit `value` as an individual error flag.
 * It then looks up the corresponding `EncoderRegisterError` definitions from a predefined
 * error mapping (`circuloEncoderRegisterErrors`) based on the provided `statusIndex`.
 *
 * @param value - The 8-bit value read from the encoder's status register (typically 0–255).
 * @param statusIndex - The index of the status register (0 or 1), usually corresponding to
 *                      `CirculoEncoderRegisterAddress.STATUS0` or `STATUS1`.
 * @returns An array of `EncoderRegisterError` objects for each set error bit in the status value.
 *          Returns an empty array if no error bits are set or no matching definitions are found.
 */
export function mapCirculoEncoderRegisterValueToErrors(value: number, statusIndex: 0 | 1): EncoderRegisterError[] {
  if (value === 0) {
    return [];
  }

  const errors: EncoderRegisterError[] = [];

  for (let bit = 1; bit <= 0x80; bit <<= 1) {
    if ((value & bit) !== 0) {
      const error = circuloEncoderRegisterErrors[statusIndex]?.[bit];
      if (error) {
        errors.push(error);
      }
    }
  }

  return errors;
}

/**
 * Represents the configuration parameters of a Circulo encoder's battery mode.
 */
export interface CirculoEncoderBatteryMode {
  maxAcceleration: number;
  maxCurrent: number;
  value: number;
}

/**
 * Maps product groups to their supported battery modes for Circulo encoders.
 *
 * Each product group key (e.g., `'Circulo 7'`, `'Circulo 9'`) maps to an array of
 * `CirculoEncoderBatteryMode` objects, each describing a specific battery mode configuration.
 *
 * Battery modes specify constraints such as maximum acceleration and current,
 * along with a raw encoded `value` representing the mode setting.
 */
export const circuloEncoderBatteryModeMap = new Map<ProductGroup, CirculoEncoderBatteryMode[]>([
  [
    'Circulo 7',
    [
      { maxAcceleration: 3.5, maxCurrent: 6, value: 0x60 },
      { maxAcceleration: 14, maxCurrent: 10, value: 0x58 },
      { maxAcceleration: 56, maxCurrent: 18, value: 0x50 },
      { maxAcceleration: 226, maxCurrent: 36, value: 0x48 },
      { maxAcceleration: 895, maxCurrent: 72, value: 0x40 },
    ],
  ],
  [
    'Circulo 9',
    [
      { maxAcceleration: 1.7, maxCurrent: 6, value: 0x60 },
      { maxAcceleration: 7, maxCurrent: 10, value: 0x58 },
      { maxAcceleration: 28, maxCurrent: 18, value: 0x50 },
      { maxAcceleration: 113, maxCurrent: 36, value: 0x48 },
      { maxAcceleration: 447, maxCurrent: 72, value: 0x40 },
    ],
  ],
]);

/**
 * Represents the possible types of encoder shafts.
 */
export type EncoderShaft = 'motor' | 'driving';

/**
 * Returns the default battery mode encoded value for a given encoder shaft type.
 *
 * @param shaft - The type of encoder shaft. Can be `'motor'` or `'driving'`.
 *                If omitted or unrecognized, returns a default fallback value.
 * @returns The encoded default battery mode value corresponding to the shaft type:
 *          - `0x5d` for `'motor'`
 *          - `0x68` for `'driving'`
 *          - `0x00` if the shaft type is not specified or unknown
 */
export function getCirculoEncoderBatteryModeDefaultValue(shaft?: 'motor' | 'driving') {
  if (shaft === 'motor') {
    return 0x5d;
  } else if (shaft === 'driving') {
    return 0x68;
  } else {
    return 0x00;
  }
}

/**
 * Retrieves the available battery mode options for a Circulo encoder product.
 *
 * This function identifies the product by its ID or hardware description and, if the product
 * belongs to the 'Circulo' family, returns an array of battery mode options specific to the product group.
 * Each option consists of a human-readable key describing the mode and its corresponding encoded value.
 *
 * The returned array always includes a "Default" option based on the provided shaft type.
 *
 * @param productId - The identifier of the product. Can be a number, string, or a `HardwareDescription` object.
 * @param shaft - Optional encoder shaft type (`'motor'` or `'driving'`) used to determine the default battery mode.
 * @returns An array of battery mode options with keys describing the mode and their associated encoded values.
 *          Returns an empty array if the product is not part of the Circulo family or no modes are found.
 */
export function getCirculoEncoderBatteryModeOptions(
  productId: number | string | HardwareDescription,
  shaft?: EncoderShaft
): { key: string; value: number }[] {
  const product = findSomanetProductById(resolveEncoderProductId(productId));
  if (product?.family === 'Circulo') {
    const arr = circuloEncoderBatteryModeMap.get(product.group);
    if (arr) {
      const defaultValue = getCirculoEncoderBatteryModeDefaultValue(shaft);
      return [
        { key: 'Default', value: defaultValue },
        ...arr.map(({ maxAcceleration, maxCurrent, value }) => ({
          key: `${maxAcceleration} kRPM/s | ${maxCurrent} μA`,
          value,
        })),
      ];
    }
  }
  return [];
}

export type EncoderConfigurationSubitemSetBy = 'none' | 'user' | 'firmware' | 'both';
export type EncoderConfigurationExceptionCase = 'case1' | 'case2' | 'case3' | 'case4';

/**
 * Represents the usage of encoder configurations across different firmware versions and products.
 *
 * This interface defines which subitems are available for each supported encoder type,
 * and specifies whether each subitem is set by the user, the firmware, or both.
 * It also maps these configurations to the relevant products and ports.
 *
 * The structure originates from the following documents:
 * - Encoder Configuration Types Supported
 * - Encoder configuration 0x2110/0x2112 subindex usage (default)
 * - Encoder configuration 0x2110/0x2112 subindex usage (exceptions)
 */
export interface EncoderConfigurationUsage {
  // Firmware version
  [key: string]: {
    encoderConfiguration: {
      encoderType: {
        // Encoder type. Only the types the default document defines are here: the internal encoder (type 5)
        // is defined by `exceptions` instead, so it has no entry at all.
        [key: number]: {
          subitem: {
            // Subindex
            [key: number]: {
              setBy: EncoderConfigurationSubitemSetBy;
            };
          };
        };
      };
      exceptions: {
        // Exception case: 'case1', 'case2', 'case3', 'case4'
        [key in EncoderConfigurationExceptionCase]: {
          subitem: {
            // Subindex
            [key: number]: {
              setBy: EncoderConfigurationSubitemSetBy;
            };
          };
        };
      };
    };
    product: {
      // Versioned product ID
      [key: string]: {
        port: {
          // Port ordinal, counted from 1, as a drive reports it in subindex 1 of its encoder configuration
          [key: number]: {
            allowedEncoderTypes: number[]; // List of allowed encoder types for this port
          };
        };
      };
    };
  };
}

/**
 * Selects the encoder configuration exception case based on the encoder ordinal, type, and product ID
 *
 * A Jasper drive is always case 4, whatever its product ID, encoder port and encoder type, so the firmware
 * version is checked first and on its own.
 *
 * @param productId - The product ID to check against.
 * @param encoderPort - The encoder port to check.
 * @param encoderType - The encoder type to check.
 * @param firmwareMajorMinorVersion - The firmware version in major.minor format.
 * @returns The selected exception case or undefined if no case matches.
 * @link https://docs.google.com/spreadsheets/d/1E0HTiZQtGmby2BJ9kOGV0UwD3HT6xQ7QRLjNbXpx5V0/edit?usp=sharing
 */
export function getEncoderConfigurationExceptionCase(
  productId: number | string | HardwareDescription | null,
  encoderPort: number,
  encoderType: number,
  firmwareMajorMinorVersion: string
): EncoderConfigurationExceptionCase | undefined {
  const id = resolveEncoderProductId(productId);
  const firmwareVersion = `${firmwareMajorMinorVersion}.0`;

  // Case 4: it's a Jasper drive. Jasper is v10 and every board that runs it has the same integrated encoder,
  // so the firmware version alone decides the case and none of the pre-Jasper cases below can apply.
  if (isJasperFirmware(firmwareVersion)) {
    return 'case4';
  }

  // Case 1
  const case1EncoderTypes = satisfies(firmwareVersion, '>=5.0 <5.1') ? [4] : [4, 5];
  if (
    satisfies(firmwareVersion, '>=5.0') &&
    isSomanetProductIdInRange(id, 'Circulo All') &&
    isEncoderPortUsable(encoderPort, id) &&
    isEncoderPortInternal(encoderPort, id) &&
    case1EncoderTypes.includes(encoderType)
  ) {
    return 'case1';
  }

  // Case 2
  if (
    satisfies(firmwareVersion, '>=5.1') &&
    isSomanetProductIdInRange(id, 'Circulo Safe Motion') &&
    isEncoderPortUsable(encoderPort, id) &&
    !isEncoderPortInternal(encoderPort, id) &&
    encoderType === 4
  ) {
    return 'case2';
  }

  // Case 3
  if (
    satisfies(firmwareVersion, '>=5.2') &&
    isSomanetProductIdInRange(id, 'Integro') &&
    isEncoderPortUsable(encoderPort, id) &&
    isEncoderPortInternal(encoderPort, id) &&
    encoderType === 5
  ) {
    return 'case3';
  }

  return;
}

/**
 * Computes the encoder resolution in bits from the total number of increments per revolution.
 *
 * The resolution in bits is calculated as the ceiling of the base-2 logarithm
 * of the number of increments, which represents the minimum number of bits
 * needed to uniquely represent each encoder position.
 *
 * @param increments - Total number of increments (counts) per revolution.
 * @returns The encoder resolution in bits.
 */
export function getEncoderResolutionBits(increments: number): number {
  return Math.ceil(Math.log2(increments));
}
